import { useEffect, useState, type ReactNode } from "react";
import { AlertTriangle, Info } from "lucide-react";
import { Button } from "../design-system/Button";
import { Modal } from "../design-system/Modal";

export type ConfirmTone = "danger" | "info";

interface ConfirmOptions {
  title: ReactNode;
  message: ReactNode;
  tone?: ConfirmTone;
  confirmLabel?: string;
  cancelLabel?: string;
}

interface ConfirmState extends ConfirmOptions {
  resolve: (value: boolean) => void;
}

/**
 * Promise-based confirmation dialog. Used by destructive actions
 * (delete, archive, hard-reset) and any other action that the user
 * should consciously confirm before it executes.
 *
 * Usage:
 *
 *   const confirm = useConfirm();
 *   ...
 *   if (await confirm({ title: "...", message: "..." })) {
 *     await doDelete();
 *   }
 *
 * Internally a single ConfirmHost component is mounted once at the
 * app root; the hook just publishes the dialog state to it.
 */

let push: ((opts: ConfirmOptions) => Promise<boolean>) | null = null;

export function confirm(opts: ConfirmOptions): Promise<boolean> {
  if (!push) {
    // Should never happen because <ConfirmHost /> is mounted at the
    // app root. Fall back to a window.confirm so the calling code
    // still works in tests or rare edge cases.
    return Promise.resolve(window.confirm(String(opts.message)));
  }
  return push(opts);
}

export function ConfirmHost() {
  const [state, setState] = useState<ConfirmState | null>(null);

  useEffect(() => {
    push = (opts) =>
      new Promise<boolean>((resolve) => {
        setState({ ...opts, resolve });
      });
    return () => {
      push = null;
    };
  }, []);

  const close = (value: boolean) => {
    state?.resolve(value);
    setState(null);
  };

  return (
    <Modal
      open={state !== null}
      onClose={() => close(false)}
      title={
        <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
          {state?.tone === "danger" ? (
            <AlertTriangle size={18} aria-hidden="true" style={{ color: "#b91c1c" }} />
          ) : (
            <Info size={18} aria-hidden="true" style={{ color: "#15803d" }} />
          )}
          {state?.title}
        </span>
      }
      size="sm"
      footer={
        <>
          <Button variant="secondary" onClick={() => close(false)}>
            {state?.cancelLabel ?? "Бекор қилиш"}
          </Button>
          <Button
            variant={state?.tone === "danger" ? "danger" : "primary"}
            onClick={() => close(true)}
            autoFocus
          >
            {state?.confirmLabel ?? "Тасдиқлаш"}
          </Button>
        </>
      }
    >
      {state?.message}
    </Modal>
  );
}
